import React, { createContext, useContext } from "react"
import { useCookieConsent } from "../hooks/cookie_consent"
import { CookieBanner } from "../components/cookie_banner"

/**
 * @typedef {Object} CookieConsentContextValue
 * @property {"pending"|"accepted"|"rejected"} status - Current consent status
 * @property {boolean} showBanner - Boolean indicating if the banner should be displayed
 * @property {"us"|"international"} region - Detected user region
 * @property {() => void} acceptAll - Accept all cookies
 * @property {() => void} rejectNonEssential - Reject non-essential cookies
 * @property {() => void} openPreferences - Open the cookie preferences banner
 * @property {() => void} closeBanner - Close the banner without changing consent
 */

/** @type {React.Context<CookieConsentContextValue|null>} */
const CookieConsentContext = createContext(null)

/**
 * Provider component that manages cookie consent state and renders the banner.
 * Uses useCookieConsent hook internally and exposes all consent functionality
 * through React context.
 *
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 * @returns {React.ReactElement} Provider component with banner
 */
export function CookieConsentProvider({ children }) {
  const consent = useCookieConsent()

  return (
    <CookieConsentContext.Provider value={consent}>
      {children}
      <CookieBanner
        visible={consent.showBanner}
        region={consent.region}
        onAccept={consent.acceptAll}
        onReject={consent.rejectNonEssential}
        onClose={consent.closeBanner}
      />
    </CookieConsentContext.Provider>
  )
}

/**
 * Hook to access cookie consent context.
 * Must be used within a CookieConsentProvider.
 *
 * @returns {CookieConsentContextValue} Cookie consent context value
 * @throws {Error} When used outside of CookieConsentProvider
 */
export function useCookieConsentContext() {
  const context = useContext(CookieConsentContext)

  if (context === null) {
    throw new Error(
      "useCookieConsentContext must be used within a CookieConsentProvider"
    )
  }

  return context
}
